// This is an admin dashboard where you can interact and visualize API routes/SQL tables

import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../contexts/GlobalContext';
import axios from '../utils/axiosConfig';
import AdminApplicationStats from './AdminApplicationStats';
import AdminJobBoard from './AdminJobBoard';
import AdminQuests from './AdminQuests';
import AdminQuestStats from './AdminQuestStats';
import AdminTrialManagement from './AdminTrialManagement';
import AdminUsersStats from './AdminUsersStats';

// Add CSV download utility function
const downloadCSV = (data, filename) => {
  // Convert data to CSV string
  const csvContent = data.map((row) => Object.values(row).join(',')).join('\n');

  // Create blob and download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const AdminDashboard = () => {
  // State for active tab
  const [activeTab, setActiveTab] = useState('super_admin');

  // State for admin info
  const [adminInfo, setAdminInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewAsAdminType, setViewAsAdminType] = useState(null);
  const [viewAsUniversityId, setViewAsUniversityId] = useState(null);

  // State for partner university form
  const [universityName, setUniversityName] = useState('');
  const [universityDomain, setUniversityDomain] = useState('');
  const [universityEmailDomain, setUniversityEmailDomain] = useState('');  // Kept for backwards compatibility
  const [universityEmailDomains, setUniversityEmailDomains] = useState([]);  // Array of domain objects
  const [emailDomainInput, setEmailDomainInput] = useState('');  // Temp input for adding domains
  const [emailDomainType, setEmailDomainType] = useState('GENERAL');  // Type for the domain being added
  const [authType, setAuthType] = useState('password');
  const [allowOpenRegistration, setAllowOpenRegistration] = useState(false);
  const [maxUsers, setMaxUsers] = useState('');

  // State for authorized user form
  const [universityID, setUniversityID] = useState('');
  const [authorizedUserEmail, setAuthorizedUserEmail] = useState('');

  // State for data tables
  const [universities, setUniversities] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [applicationStatuses, setApplicationStatuses] = useState([]);
  const [schoolUsers, setSchoolUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [showAddAdminForm, setShowAddAdminForm] = useState(false);
  const [selectedUserForNewAdmin, setSelectedUserForNewAdmin] = useState('');
  const [newAdminType, setNewAdminType] = useState('school_admin');
  const [newAdminUniversity, setNewAdminUniversity] = useState('');

  // State for form feedback
  const [universityFormMessage, setUniversityFormMessage] = useState('');
  const [userFormMessage, setUserFormMessage] = useState('');

  // State for expandable forms
  const [showUniversityForm, setShowUniversityForm] = useState(false);
  const [showAuthorizedUserForm, setShowAuthorizedUserForm] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [selectedUserForAdmin, setSelectedUserForAdmin] = useState(null);
  const [adminModalMessage, setAdminModalMessage] = useState('');

  // Get this user
  const { userProfile } = useContext(GlobalContext);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!userProfile) return;

      try {
        const response = await axios.get(`/api/v1/user/${userProfile.user_id}/is-admin`);
        setAdminInfo(response.data);

        // If user is a school admin, automatically select their school
        if (
          response.data.is_admin &&
          response.data.admin_type === 'school_admin' &&
          response.data.university_id
        ) {
          setSelectedSchool(response.data.university_id.toString());
          fetchApplicationStatuses(response.data.university_id);
          fetchSchoolUsers(response.data.university_id);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdminStatus();
  }, [userProfile]);

  // Fetch partner universities
  const fetchUniversities = async () => {
    try {
      const response = await axios.get('/api/v1/university');
      const universities = response.data.universities || [];

      // Filter universities for school admins
      if (adminInfo?.is_admin && adminInfo?.admin_type === 'school_admin') {
        const filteredUniversities = universities.filter(
          (uni) => uni.university_id === adminInfo.university_id
        );
        setUniversities(filteredUniversities);
      } else {
        setUniversities(universities);
      }
    } catch (error) {
      console.error('Error fetching universities:', error);
    }
  };

  // Fetch users
  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/v1/user');
      const usersData = response.data.users || [];

      // Fetch quests for all users
      const questsPromises = usersData.map((user) =>
        axios
          .get(`/api/v1/quest/user/${user.user_id}`)
          .then((response) => ({ userId: user.user_id, hasQuest: true }))
          .catch(() => ({ userId: user.user_id, hasQuest: false }))
      );

      const questResults = await Promise.all(questsPromises);
      const questMap = new Map(questResults.map((result) => [result.userId, result.hasQuest]));

      // Create a map of authorized users by email
      const authorizedUserMap = new Map(authorizedUsers.map((au) => [au.authorized_email, au]));

      // Add quest status and university info to users
      const usersWithQuests = usersData.map((user) => ({
        ...user,
        quest_accepted: questMap.get(user.user_id) || false,
        university_id: authorizedUserMap.get(user.email)?.university_id || null,
      }));

      setUsers(usersWithQuests);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Fetch authorized users
  const fetchAuthorizedUsers = async () => {
    try {
      const response = await axios.get('/api/v1/authorized-user');
      setAuthorizedUsers(response.data.authorized_users || []);
      console.log('Authorized users data:', response.data.authorized_users);
    } catch (error) {
      console.error('Error fetching authorized users:', error);
    }
  };

  // Fetch roles
  const fetchRoles = async () => {
    try {
      const response = await axios.get('/api/v1/role');
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  // Fetch application statuses for a school
  const fetchApplicationStatuses = async (universityId) => {
    try {
      const response = await axios.get(`/api/v1/application/university/${universityId}/status`);
      setApplicationStatuses(response.data.statuses || []);
    } catch (error) {
      console.error('Error fetching application statuses:', error);
    }
  };

  // Fetch users for a school
  const fetchSchoolUsers = async (universityId) => {
    try {
      const response = await axios.get(`/api/v1/user/school/${universityId}`);
      setSchoolUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching school users:', error);
    }
  };

  // Fetch admin users
  const fetchAdminUsers = async () => {
    try {
      const response = await axios.get('/api/v1/user/admins');
      setAdminUsers(response.data.admins || []);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  // Handle school selection change
  const handleSchoolChange = (event) => {
    const universityId = event.target.value;
    setSelectedSchool(universityId);
    if (universityId) {
      fetchApplicationStatuses(universityId);
      fetchSchoolUsers(universityId);
    } else {
      setApplicationStatuses([]);
      setSchoolUsers([]);
    }
  };

  // Update selected school when view changes
  useEffect(() => {
    if (viewAsUniversityId) {
      setSelectedSchool(viewAsUniversityId);
      fetchApplicationStatuses(viewAsUniversityId);
      fetchSchoolUsers(viewAsUniversityId);
    } else if (viewAsAdminType === 'super_admin') {
      setSelectedSchool('');
      setApplicationStatuses([]);
      setSchoolUsers([]);
    }
  }, [viewAsUniversityId, viewAsAdminType]);

  // Load data on component mount
  useEffect(() => {
    fetchUniversities();
    fetchUsers();
    fetchAuthorizedUsers();
    fetchRoles();
    fetchAdminUsers();
  }, []);

  // Add partner university
  const addApplication = async (event) => {
    event.preventDefault();
    setUniversityFormMessage('');

    try {
      // Use universityEmailDomains array if populated, otherwise fall back to single domain
      const emailDomainsToSend = universityEmailDomains.length > 0
        ? universityEmailDomains
        : (universityEmailDomain ? [{ email_domain: universityEmailDomain, domain_type: 'GENERAL' }] : []);

      await axios.post('/api/v1/university/create', {
        university: {
          name: universityName,
          domain: universityDomain,
          email_domains: emailDomainsToSend,
          auth_type: authType,
          allow_open_registration: allowOpenRegistration,
          max_users: maxUsers ? parseInt(maxUsers) : null,
        }
      });

      // If we reach here, the request was successful (axios throws on error)
      setUniversityFormMessage('University added successfully!');
      // Clear form
      setUniversityName('');
      setUniversityDomain('');
      setUniversityEmailDomain('');
      setUniversityEmailDomains([]);
      setEmailDomainInput('');
      setEmailDomainType('GENERAL');
      setAuthType('password');
      setAllowOpenRegistration(false);
      setMaxUsers('');
      // Refresh universities list
      fetchUniversities();
      // Close the form
      setShowUniversityForm(false);
    } catch (error) {
      console.error('Error adding university:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to add university';
      setUniversityFormMessage(`Error: ${errorMessage}`);
    }
  };

  // Add authorized user
  const addAuthorizedUser = async (event) => {
    event.preventDefault();
    setUserFormMessage('');

    try {
      const authorizedUserData = {
        authorized_user: {
          university_id: universityID,
          authorized_email: authorizedUserEmail,
        },
      };

      const response = await axios.post('/api/v1/authorized-user/create', authorizedUserData);

      const data = response.data;

      if (response.ok) {
        setUserFormMessage('Authorized user added successfully!');
        // Clear form
        setUniversityID('');
        setAuthorizedUserEmail('');
        // Refresh authorized users list
        fetchAuthorizedUsers();
        // Close the form
        setShowAuthorizedUserForm(false);
      } else {
        setUserFormMessage(`Error: ${data.message || 'Failed to add authorized user'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      setUserFormMessage('Error adding authorized user. Please try again.');
    }
  };

  // Add this function near the top of the component, after the state declarations
  const handleDeleteUser = async (userId) => {
    if (
      !window.confirm('Are you sure you want to delete this user? This action cannot be undone.')
    ) {
      return;
    }

    try {
      await axios.delete(`/api/v1/user/${userId}`);
      // Refresh the users list
      fetchUsers();
      alert('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(`Error deleting user: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Handle making a user an admin
  const handleMakeAdmin = async (userId, adminType, universityId = null) => {
    try {
      const response = await axios.put(`/api/v1/user/${userId}/admin`, {
        admin_type: adminType,
        university_id: universityId,
      });

      if (response.data) {
        setAdminModalMessage('Admin status updated successfully!');
        // Refresh both users and admin users lists
        fetchUsers();
        fetchAdminUsers();
      }
    } catch (error) {
      console.error('Error updating admin status:', error);
      setAdminModalMessage(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Add this function near the other handler functions
  const handleRemoveAdmin = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user's admin status?")) {
      return;
    }

    try {
      await axios.delete(`/api/v1/user/${userId}/admin`);
      // Refresh the admin users list
      fetchAdminUsers();
      alert('Admin status removed successfully');
    } catch (error) {
      console.error('Error removing admin status:', error);
      alert(`Error removing admin status: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Check if user is an admin
  if (!adminInfo?.is_admin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6">
        <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full text-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-16 w-16 text-red-500 mx-auto mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">
            You are not authorized to access this admin dashboard.
          </p>
          <a
            href="/"
            className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-sm transition duration-200"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  // Determine if we should show super admin features
  const effectiveAdminType = viewAsAdminType || adminInfo.admin_type;
  const effectiveUniversityId = viewAsUniversityId || adminInfo.university_id;
  const isSuperAdmin = adminInfo.admin_type === 'super_admin';

  // Debug information
  console.log('Admin Info:', adminInfo);
  console.log('Is Super Admin:', isSuperAdmin);
  console.log('Effective Admin Type:', effectiveAdminType);
  console.log('Effective University ID:', effectiveUniversityId);

  return (
    <div className="bg-gray-50 text-gray-800 min-h-screen p-6">
      <div className="max-w-340 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-xl text-gray-800 font-bold sm:text-3xl lg:text-4xl lg:leading-tight">
            Admin Dashboard for{' '}
            {adminInfo.admin_type === 'school_admin'
              ? `${universities.find((u) => u.university_id === adminInfo.university_id)?.name || 'Unknown School'}`
              : adminInfo.admin_type}
          </h1>
          {/* View switcher - only show for actual super admins */}
          {adminInfo.admin_type === 'super_admin' && (
            <div className="mt-4 flex justify-center">
              <div className="inline-flex items-center space-x-3 bg-white rounded-lg shadow-md p-3 border border-gray-200">
                <span className="text-sm font-medium text-gray-700">View as:</span>
                <div className="relative">
                  <select
                    value={viewAsAdminType || 'super_admin'}
                    onChange={(e) => {
                      setViewAsAdminType(e.target.value);
                      if (e.target.value === 'super_admin') {
                        setViewAsUniversityId(null);
                      }
                    }}
                    className="appearance-none bg-white pl-3 pr-10 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm font-medium text-gray-700 cursor-pointer hover:border-gray-400 transition-colors"
                  >
                    <option value="super_admin">Super Admin</option>
                    <option value="school_admin">School Admin</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
                {viewAsAdminType === 'school_admin' && (
                  <div className="relative">
                    <select
                      value={viewAsUniversityId || ''}
                      onChange={(e) => setViewAsUniversityId(e.target.value)}
                      className="appearance-none bg-white pl-3 pr-10 py-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm font-medium text-gray-700 cursor-pointer hover:border-gray-400 transition-colors min-w-[200px]"
                    >
                      <option value="">Select School...</option>
                      {universities.map((university) => (
                        <option key={university.university_id} value={university.university_id}>
                          {university.name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                )}
                {viewAsAdminType && (
                  <button
                    onClick={() => {
                      setViewAsAdminType(null);
                      setViewAsUniversityId(null);
                    }}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <svg
                      className="h-4 w-4 mr-1.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Reset
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Tabs - only show when viewing as super admin */}
        {effectiveAdminType === 'super_admin' && !viewAsAdminType && (
          <div className="max-w-7xl mx-auto mb-8">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab('super_admin')}
                  className={`${
                    activeTab === 'super_admin'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Super Admin Dashboard
                </button>
                <button
                  onClick={() => setActiveTab('schools')}
                  className={`${
                    activeTab === 'schools'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  School Stats
                </button>
                <button
                  onClick={() => setActiveTab('quests')}
                  className={`${
                    activeTab === 'quests'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Quests
                </button>
                <button
                  onClick={() => setActiveTab('job_board')}
                  className={`${
                    activeTab === 'job_board'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Job Board
                </button>
                <button
                  onClick={() => setActiveTab('trials')}
                  className={`${
                    activeTab === 'trials'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                >
                  Trial Management
                </button>
              </nav>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="max-w-7xl mx-auto">
          {effectiveAdminType === 'super_admin' && !viewAsAdminType ? (
            // Super admin viewing as super admin
            activeTab === 'super_admin' ? (
              // Super Admin Dashboard
              <div className="grid grid-cols-1 gap-6">
                {/* Partner Universities Table */}
                <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-blue-600">Partner Universities</h3>
                    <button
                      onClick={() => {
                        const headers = [
                          'ID',
                          'Name',
                          'Domain',
                          'Email Domain',
                          'Auth Type',
                          'Open Reg',
                          'Max Users',
                        ];
                        const csvData = universities.map((uni) => ({
                          ID: uni.university_id,
                          Name: uni.name,
                          Domain: uni.domain,
                          'Email Domain': uni.email_domain,
                          'Auth Type': uni.auth_type,
                          'Open Reg': uni.allow_open_registration ? 'Yes' : 'No',
                          'Max Users': uni.max_users || 'No limit',
                        }));
                        downloadCSV(
                          [headers, ...csvData.map((row) => Object.values(row))],
                          'partner_universities.csv'
                        );
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      Download CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Domain
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email Domain
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Auth Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Open Reg
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Max Users
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {universities.length > 0 ? (
                          universities.map((university) => (
                            <tr key={university.university_iD} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap">
                                {university.university_id}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">{university.name}</td>
                              <td className="px-4 py-3 whitespace-nowrap">{university.domain}</td>
                              <td className="px-4 py-3">
                                {university.email_domains && university.email_domains.length > 1 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {university.email_domains.map((domain, idx) => {
                                      // Extract email_domain string from object
                                      const domainStr = typeof domain === 'string' ? domain : domain.email_domain;
                                      return (
                                        <span
                                          key={idx}
                                          className="inline-block px-2 py-1 text-xs bg-gray-100 rounded-sm"
                                        >
                                          {domainStr}
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  university.email_domain
                                )}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {university.auth_type}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {university.allow_open_registration ? 'Yes' : 'No'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {university.max_users || 'No limit'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="px-4 py-3 text-center text-gray-500">
                              No universities found
                            </td>
                          </tr>
                        )}

                        {/* Add University Form Row */}
                        <tr className="bg-gray-50">
                          <td colSpan="7" className="px-4 py-3">
                            <button
                              onClick={() => setShowUniversityForm(!showUniversityForm)}
                              className="w-full text-left text-blue-600 hover:text-blue-800 font-medium flex items-center"
                            >
                              {showUniversityForm ? (
                                <span className="mr-2">▼</span>
                              ) : (
                                <span className="mr-2">▶</span>
                              )}
                              {showUniversityForm
                                ? 'Hide Add University Form'
                                : 'Add New University (REMEMBER TO UPDATE LINKEDIN CONFIGS)'}
                            </button>

                            {showUniversityForm && (
                              <div className="mt-4 p-4 bg-white rounded-md border border-gray-200">
                                <h4 className="text-lg font-medium mb-3 text-blue-600">
                                  Add Partner University
                                </h4>
                                <form className="flex flex-col gap-4" onSubmit={addApplication}>
                                  <div className="flex flex-col">
                                    <label className="mb-1 font-medium text-gray-700">
                                      University Name:
                                    </label>
                                    <input
                                      type="text"
                                      className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                      value={universityName}
                                      onChange={(e) => setUniversityName(e.target.value)}
                                      required
                                    />
                                  </div>
                                  <div className="flex flex-col">
                                    <label className="mb-1 font-medium text-gray-700">
                                      University Domain:
                                    </label>
                                    <input
                                      type="text"
                                      className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                      value={universityDomain}
                                      onChange={(e) => setUniversityDomain(e.target.value)}
                                      required
                                    />
                                  </div>
                                  <div className="flex flex-col">
                                    <label className="mb-1 font-medium text-gray-700">
                                      University Email Domains:
                                    </label>
                                    <div className="space-y-2">
                                      {universityEmailDomains.length > 0 && (
                                        <div className="flex flex-wrap gap-2 p-2 bg-gray-50 rounded-md">
                                          {universityEmailDomains.map((domainObj, index) => (
                                            <span
                                              key={index}
                                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                            >
                                              {domainObj.email_domain}
                                              <span className="ml-1 text-xs text-blue-600">({domainObj.domain_type})</span>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setUniversityEmailDomains(
                                                    universityEmailDomains.filter((_, i) => i !== index)
                                                  );
                                                }}
                                                className="ml-2 text-blue-600 hover:text-blue-800 font-bold"
                                              >
                                                ×
                                              </button>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                      <div className="flex gap-2">
                                        <input
                                          type="text"
                                          className="flex-1 p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                          value={emailDomainInput}
                                          onChange={(e) => setEmailDomainInput(e.target.value)}
                                          onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              const trimmedDomain = emailDomainInput.trim();
                                              if (trimmedDomain && !universityEmailDomains.some(d => d.email_domain === trimmedDomain)) {
                                                setUniversityEmailDomains([...universityEmailDomains, {
                                                  email_domain: trimmedDomain,
                                                  domain_type: emailDomainType
                                                }]);
                                                setEmailDomainInput('');
                                              }
                                            }
                                          }}
                                          placeholder="e.g., mail.uc.edu"
                                        />
                                        <select
                                          className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                          value={emailDomainType}
                                          onChange={(e) => setEmailDomainType(e.target.value)}
                                        >
                                          <option value="GENERAL">General</option>
                                          <option value="STUDENT">Student</option>
                                          <option value="STAFF">Staff</option>
                                          <option value="FACULTY">Faculty</option>
                                          <option value="ALUMNI">Alumni</option>
                                          <option value="DEPARTMENT">Department</option>
                                        </select>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const trimmedDomain = emailDomainInput.trim();
                                            if (trimmedDomain && !universityEmailDomains.some(d => d.email_domain === trimmedDomain)) {
                                              setUniversityEmailDomains([...universityEmailDomains, {
                                                email_domain: trimmedDomain,
                                                domain_type: emailDomainType
                                              }]);
                                              setEmailDomainInput('');
                                            }
                                          }}
                                          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                        >
                                          Add
                                        </button>
                                      </div>
                                      <p className="text-xs text-gray-500">
                                        Add multiple email domains with types for universities with different domains for students/staff
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex flex-col">
                                    <label className="mb-1 font-medium text-gray-700">
                                      Auth Type:
                                    </label>
                                    <select
                                      className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                      value={authType}
                                      onChange={(e) => setAuthType(e.target.value)}
                                    >
                                      <option value="password">Password</option>
                                      <option value="google_oauth">Google OAuth</option>
                                      <option value="university_sso">University SSO</option>
                                    </select>
                                  </div>
                                  <div className="flex items-center">
                                    <input
                                      type="checkbox"
                                      id="allowOpenRegistration"
                                      className="mr-2 h-4 w-4 text-blue-600 border-gray-300 rounded-sm focus:ring-blue-500"
                                      checked={allowOpenRegistration}
                                      onChange={(e) => setAllowOpenRegistration(e.target.checked)}
                                    />
                                    <label
                                      htmlFor="allowOpenRegistration"
                                      className="font-medium text-gray-700"
                                    >
                                      Allow Open Registration (anyone with valid email domain can
                                      register)
                                    </label>
                                  </div>
                                  {allowOpenRegistration && (
                                    <div className="flex flex-col">
                                      <label className="mb-1 font-medium text-gray-700">
                                        Max Users (optional):
                                      </label>
                                      <input
                                        type="number"
                                        className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                        value={maxUsers}
                                        onChange={(e) => setMaxUsers(e.target.value)}
                                        placeholder="Leave empty for no limit"
                                        min="1"
                                      />
                                    </div>
                                  )}
                                  <div className="flex justify-between">
                                    <button
                                      type="submit"
                                      className="p-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
                                    >
                                      Add University
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setShowUniversityForm(false)}
                                      className="p-2 rounded-md bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                  {universityFormMessage && (
                                    <p
                                      className={`mt-2 text-sm ${universityFormMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}`}
                                    >
                                      {universityFormMessage}
                                    </p>
                                  )}
                                </form>
                              </div>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Authorized Users Table */}
                <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-blue-600">Authorized Users</h3>
                    <button
                      onClick={() => {
                        const headers = ['University ID', 'Email', 'Status'];
                        const csvData = authorizedUsers.map((user) => ({
                          'University ID': user.university_id,
                          Email: user.authorized_email,
                          Status: user.claimed ? 'Claimed' : 'Available',
                        }));
                        downloadCSV(
                          [headers, ...csvData.map((row) => Object.values(row))],
                          'authorized_users.csv'
                        );
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      Download CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            University ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {authorizedUsers.length > 0 ? (
                          authorizedUsers.map((user) => (
                            <tr key={user.authorized_email} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap">{user.university_id}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {user.authorized_email}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {user.claimed ? 'Claimed' : 'Available'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="px-4 py-3 text-center text-gray-500">
                              No authorized users found
                            </td>
                          </tr>
                        )}

                        {/* Add Authorized User Form Row */}
                        <tr className="bg-gray-50">
                          <td colSpan="4" className="px-4 py-3">
                            <button
                              onClick={() => setShowAuthorizedUserForm(!showAuthorizedUserForm)}
                              className="w-full text-left text-blue-600 hover:text-blue-800 font-medium flex items-center"
                            >
                              {showAuthorizedUserForm ? (
                                <span className="mr-2">▼</span>
                              ) : (
                                <span className="mr-2">▶</span>
                              )}
                              {showAuthorizedUserForm
                                ? 'Hide Add Authorized User Form'
                                : 'Add New Authorized User'}
                            </button>

                            {showAuthorizedUserForm && (
                              <div className="mt-4 p-4 bg-white rounded-md border border-gray-200">
                                <h4 className="text-lg font-medium mb-3 text-blue-600">
                                  Add Authorized User
                                </h4>
                                <form className="flex flex-col gap-4" onSubmit={addAuthorizedUser}>
                                  <div className="flex flex-col">
                                    <label className="mb-1 font-medium text-gray-700">
                                      Select University:
                                    </label>
                                    <select
                                      className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                      value={universityID}
                                      onChange={(e) => setUniversityID(e.target.value)}
                                      required
                                    >
                                      <option value="">Select a university</option>
                                      {universities.map((university) => (
                                        <option
                                          key={university.university_id}
                                          value={university.university_id}
                                        >
                                          {university.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <div className="flex flex-col">
                                    <label className="mb-1 font-medium text-gray-700">
                                      Authorized User Email:
                                    </label>
                                    <input
                                      type="email"
                                      className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                      value={authorizedUserEmail}
                                      onChange={(e) => setAuthorizedUserEmail(e.target.value)}
                                      required
                                    />
                                  </div>
                                  <div className="flex justify-between">
                                    <button
                                      type="submit"
                                      className="p-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
                                    >
                                      Add Authorized User
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setShowAuthorizedUserForm(false)}
                                      className="p-2 rounded-md bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                  {userFormMessage && (
                                    <p
                                      className={`mt-2 text-sm ${userFormMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}`}
                                    >
                                      {userFormMessage}
                                    </p>
                                  )}
                                </form>
                              </div>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Users Table */}
                <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6">
                  <div className="flex justify_between items-center mb-4">
                    <h3 className="text-xl font-bold text-blue-600">All Users</h3>
                    <button
                      onClick={() => {
                        const headers = [
                          'ID',
                          'Email',
                          'First Name',
                          'Middle Name',
                          'Last Name',
                          'School',
                          'Last Login',
                          'Created At',
                          'Quest Accepted',
                        ];
                        const csvData = users.map((user) => ({
                          ID: user.user_id,
                          Email: user.email,
                          'First Name': user.first_name,
                          'Middle Name': user.middle_name || '',
                          'Last Name': user.last_name,
                          School:
                            universities.find((u) => u.university_id === user.university_id)
                              ?.name || 'Unknown',
                          'Last Login': user.last_login_at
                            ? new Date(user.last_login_at).toLocaleString()
                            : 'Never',
                          'Created At': user.created_at
                            ? new Date(user.created_at).toLocaleString()
                            : 'Unknown',
                          'Quest Accepted': user.quest_accepted ? 'Yes' : 'No',
                        }));
                        downloadCSV(
                          [headers, ...csvData.map((row) => Object.values(row))],
                          'all_users.csv'
                        );
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      Download CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            First Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Middle Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            School
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Login
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created At
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Quest Accepted
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {users.length > 0 ? (
                          users.map((user) => (
                            <tr key={user.userID} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap">{user.user_id}</td>
                              <td className="px-4 py-3 whitespace-nowrap">{user.email}</td>
                              <td className="px-4 py-3 whitespace-nowrap">{user.first_name}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {user.middle_name || '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">{user.last_name}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {universities.find((u) => u.university_id === user.university_id)
                                  ?.name || 'Unknown'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {user.last_login_at
                                  ? new Date(user.last_login_at).toLocaleString()
                                  : 'Never'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {user.created_at
                                  ? new Date(user.created_at).toLocaleString()
                                  : 'Unknown'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {user.quest_accepted ? 'Yes' : 'No'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap space-x-2">
                                <button
                                  onClick={() => handleDeleteUser(user.user_id)}
                                  className="text-red-600 hover:text-red-800 font-medium"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="10" className="px-4 py-3 text-center text-gray-500">
                              No users found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Admin Users Table */}
                <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6 mt-6">
                  <div className="flex justify_between items-center mb-4">
                    <h3 className="text-xl font-bold text-blue-600">Admin Users</h3>
                    <button
                      onClick={() => {
                        const headers = ['User ID', 'Email', 'Name', 'Admin Type', 'School'];
                        const csvData = adminUsers.map((admin) => ({
                          'User ID': admin.user.user_id,
                          Email: admin.user.email,
                          Name: `${admin.user.first_name} ${admin.user.middle_name ? admin.user.middle_name + ' ' : ''}${admin.user.last_name}`,
                          'Admin Type': admin.admin_type,
                          School:
                            admin.admin_type === 'school_admin'
                              ? universities.find((u) => u.university_id === admin.university_id)
                                  ?.name || 'Unknown School'
                              : '-',
                        }));
                        downloadCSV(
                          [headers, ...csvData.map((row) => Object.values(row))],
                          'admin_users.csv'
                        );
                      }}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      Download CSV
                    </button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User ID
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Admin Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            School
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {adminUsers.length > 0 ? (
                          adminUsers.map((admin) => (
                            <tr key={admin.user.user_id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap">{admin.user.user_id}</td>
                              <td className="px-4 py-3 whitespace-nowrap">{admin.user.email}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {admin.user.first_name}{' '}
                                {admin.user.middle_name ? admin.user.middle_name + ' ' : ''}
                                {admin.user.last_name}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">{admin.admin_type}</td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                {admin.admin_type === 'school_admin'
                                  ? universities.find(
                                      (u) => u.university_id === admin.university_id
                                    )?.name || 'Unknown School'
                                  : '-'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <button
                                  onClick={() => handleRemoveAdmin(admin.user.user_id)}
                                  className="text-red-600 hover:text-red-800 font-medium"
                                >
                                  Remove Admin
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="px-4 py-3 text-center text-gray-500">
                              No admin users found
                            </td>
                          </tr>
                        )}

                        {/* Add Admin Form Row */}
                        <tr className="bg-gray-50">
                          <td colSpan="6" className="px-4 py-3">
                            <button
                              onClick={() => setShowAddAdminForm(!showAddAdminForm)}
                              className="w-full text-left text-blue-600 hover:text-blue-800 font-medium flex items-center"
                            >
                              {showAddAdminForm ? (
                                <span className="mr-2">▼</span>
                              ) : (
                                <span className="mr-2">▶</span>
                              )}
                              {showAddAdminForm ? 'Hide Add Admin Form' : 'Add New Admin'}
                            </button>

                            {showAddAdminForm && (
                              <div className="mt-4 p-4 bg-white rounded-md border border-gray-200">
                                <h4 className="text-lg font-medium mb-3 text-blue-600">
                                  Add New Admin
                                </h4>
                                <form
                                  className="flex flex-col gap-4"
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    handleMakeAdmin(
                                      parseInt(selectedUserForNewAdmin),
                                      newAdminType,
                                      newAdminType === 'school_admin'
                                        ? parseInt(newAdminUniversity)
                                        : null
                                    );
                                    setShowAddAdminForm(false);
                                    setSelectedUserForNewAdmin('');
                                    setNewAdminType('school_admin');
                                    setNewAdminUniversity('');
                                    fetchAdminUsers();
                                  }}
                                >
                                  <div className="flex flex-col">
                                    <label className="mb-1 font-medium text-gray-700">
                                      Select User:
                                    </label>
                                    <select
                                      className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                      value={selectedUserForNewAdmin}
                                      onChange={(e) => setSelectedUserForNewAdmin(e.target.value)}
                                      required
                                    >
                                      <option value="">Select a user</option>
                                      {users.map((user) => (
                                        <option key={user.user_id} value={user.user_id}>
                                          {user.first_name} {user.last_name} ({user.email})
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div className="flex flex-col">
                                    <label className="mb-1 font-medium text-gray-700">
                                      Admin Type:
                                    </label>
                                    <select
                                      className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                      value={newAdminType}
                                      onChange={(e) => setNewAdminType(e.target.value)}
                                      required
                                    >
                                      <option value="school_admin">School Admin</option>
                                      <option value="super_admin">Super Admin</option>
                                    </select>
                                  </div>

                                  {newAdminType === 'school_admin' && (
                                    <div className="flex flex-col">
                                      <label className="mb-1 font-medium text-gray-700">
                                        Select School:
                                      </label>
                                      <select
                                        className="p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                        value={newAdminUniversity}
                                        onChange={(e) => setNewAdminUniversity(e.target.value)}
                                        required
                                      >
                                        <option value="">Select a school</option>
                                        {universities.map((university) => (
                                          <option
                                            key={university.university_id}
                                            value={university.university_id}
                                          >
                                            {university.name}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  <div className="flex justify_between">
                                    <button
                                      type="submit"
                                      className="p-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium transition-colors"
                                    >
                                      Add Admin
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setShowAddAdminForm(false)}
                                      className="p-2 rounded-md bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </form>
                              </div>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : activeTab === 'schools' ? (
              // School Stats View
              <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6">
                <div className="mb-6">
                  <label
                    htmlFor="school-select"
                    className="block text-lg font-medium text-gray-700 mb-2"
                  >
                    Select School to View Stats:
                  </label>
                  <select
                    id="school-select"
                    className="w-full p-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-gray-700"
                    value={selectedSchool}
                    onChange={handleSchoolChange}
                  >
                    <option value="">Choose a school...</option>
                    {universities.map((university) => (
                      <option key={university.university_id} value={university.university_id}>
                        {university.name}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedSchool && (
                  <>
                    <AdminUsersStats
                      selectedSchool={selectedSchool}
                      users={schoolUsers}
                      isSuperAdmin={true}
                    />
                    <div className="mt-8">
                      <AdminQuestStats selectedSchool={selectedSchool} isSuperAdmin={true} />
                    </div>
                    <div className="mt-8">
                      <AdminApplicationStats
                        selectedSchool={selectedSchool}
                        applicationStatuses={applicationStatuses}
                        isSuperAdmin={true}
                      />
                    </div>
                  </>
                )}
              </div>
            ) : activeTab === 'job_board' ? (
              // Job Board View
              <AdminJobBoard />
            ) : activeTab === 'quests' ? (
              // Quests View
              <AdminQuests />
            ) : (
              // Trial Management View
              <AdminTrialManagement universities={universities} users={users} />
            )
          ) : (
            // School admin view (either actual school admin or viewing as school admin)
            <div className="bg-white border border-gray-200 shadow-md rounded-xl p-6">
              <AdminUsersStats
                selectedSchool={selectedSchool}
                users={schoolUsers}
                isSuperAdmin={false}
              />
              <div className="mt-8">
                <AdminQuestStats selectedSchool={selectedSchool} isSuperAdmin={false} />
              </div>
              <div className="mt-8">
                <AdminApplicationStats
                  selectedSchool={selectedSchool}
                  applicationStatuses={applicationStatuses}
                  isSuperAdmin={false}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Admin Modal */}
      {showAdminModal && selectedUserForAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-blue-600">
              Make {selectedUserForAdmin.first_name} {selectedUserForAdmin.last_name} an Admin
            </h3>

            <div className="space-y-4">
              <div>
                <button
                  onClick={() => handleMakeAdmin(selectedUserForAdmin.user_id, 'super_admin')}
                  className="w-full p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
                >
                  Make Super Admin
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Make School Admin
                </label>
                <select
                  className="w-full p-2 rounded-md border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  onChange={(e) => {
                    const universityId = e.target.value;
                    if (universityId) {
                      handleMakeAdmin(
                        selectedUserForAdmin.user_id,
                        'school_admin',
                        parseInt(universityId)
                      );
                    }
                  }}
                >
                  <option value="">Select a university...</option>
                  {universities.map((university) => (
                    <option key={university.university_id} value={university.university_id}>
                      {university.name}
                    </option>
                  ))}
                </select>
              </div>

              {adminModalMessage && (
                <p
                  className={`mt-2 text-sm ${adminModalMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}`}
                >
                  {adminModalMessage}
                </p>
              )}

              <div className="flex justify-end mt-4">
                <button
                  onClick={() => {
                    setShowAdminModal(false);
                    setSelectedUserForAdmin(null);
                    setAdminModalMessage('');
                  }}
                  className="p-2 rounded-md bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
